import React, { useState, useCallback, useEffect } from 'react';

const AccountsList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [originalData, setOriginalData] = useState([
    // Initial mock data

    { id: 1, name: 'Acme Corporation', type: 'Enterprise', status: 'Active', contactPerson:'John Doe', lastActive: '2025-04-25' },
    { id: 2, name: 'TechStart Inc', type: 'Startup', status: 'Active', contactPerson:'John Doe', lastActive: '2025-04-24' },
    { id: 3, name: 'Global Solutions', type: 'Enterprise', status: 'Inactive',  contactPerson:'John Doe', lastActive: '2025-04-20' },
    { id: 4, name: 'Digital Innovations', type: 'SMB', status: 'Active', contactPerson:'John Doe', lastActive: '2025-04-23' },
    { id: 5, name: 'Future Tech', type: 'Enterprise', status: 'Active', contactPerson:'Jane Smith', lastActive: '2025-04-22' },
    { id: 6, name: 'Smart Solutions', type: 'SMB', status: 'Pending', contactPerson:'Mike Johnson', lastActive: '2025-04-21' },
    { id: 7, name: 'Innovate Labs', type: 'Startup', status: 'Active', contactPerson:'Sarah Wilson', lastActive: '2025-04-19' }
  ]);
  const [displayData, setDisplayData] = useState([]);

  const emptyAccount = {
    id: 'new',
    name: '',
    type: 'Enterprise',
    status: 'Active',
    contactPerson: ''
  };

  const fetchAccounts = useCallback(() => {
    try {
      // Filter accounts based on search term
      const filteredAccounts = originalData.filter(account => 
        account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.contactPerson.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setDisplayData(filteredAccounts);
    } catch (error) {
      console.error('Error processing accounts:', error);
      setError('Failed to process accounts');
    }
  }, [originalData, searchTerm]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleAddNew = () => {
    if (isAddingNew) {
      setError('Please save or cancel the current new account first');
      return;
    }
    setIsAddingNew(true);
    setEditingId('new');
    setEditedData(emptyAccount);
    setError(null);
    setDisplayData([emptyAccount, ...displayData]);
  };

  const validateForm = () => {
    if (!editedData.name?.trim()) {
      setError('Account name is required');
      return false;
    }
    if (!editedData.contactPerson?.trim()) {
      setError('Contact person is required');
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      setError(null);
      
      if (editingId === 'new') {
        // Create new account with a generated ID
        const newAccount = {
          ...editedData,
          id: Date.now(),
          lastActive: new Date().toISOString().split('T')[0]
        };
        const updatedData = displayData.filter(acc => acc.id !== 'new');
        const newData = [newAccount, ...updatedData];
        setDisplayData(newData);
        setOriginalData(newData); // Update original data
        setIsAddingNew(false);
      } else {
        // Update existing account
        const updatedData = displayData.map(item => 
          item.id === editedData.id ? {
            ...editedData,
            lastActive: new Date().toISOString().split('T')[0]
          } : item
        );
        setDisplayData(updatedData);
        setOriginalData(updatedData); // Update original data
      }
      setEditingId(null);
      setEditedData({});
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving changes');
      console.error('Error saving account:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isAddingNew) {
      setDisplayData(displayData.filter(acc => acc.id !== 'new'));
      setIsAddingNew(false);
    }
    setEditingId(null);
    setEditedData({});
    setError(null);
  };

  const handleEdit = (item) => {
    if (isAddingNew) {
      setError('Please save or cancel the new account first');
      return;
    }
    if (editingId) {
      setError('Please save or cancel the current edit first');
      return;
    }
    setEditingId(item.id);
    setEditedData(item);
    setError(null);
  };

  const handleDelete = (item) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        setLoading(true);
        setError(null);
        const updatedData = displayData.filter(acc => acc.id !== item.id);
        setDisplayData(updatedData);
        setOriginalData(updatedData); // Update original data as well
      } catch (err) {
        setError('Error deleting account');
        console.error('Error deleting account:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (field, value) => {
    setEditedData({
      ...editedData,
      [field]: value
    });
    setError(null);
  };

  // Filtering is now handled by fetchAccounts
  const filteredData = displayData;

  return (
    <div className="container mt-4">
      {error && (
        <div className="alert alert-danger mb-4" role="alert">
          {error}
        </div>
      )}
      
      <div className="listing-header d-flex justify-content-between align-items-center">
        <h2 className="listing-title">Accounts</h2>
        <button 
          className="btn-add-new"
          onClick={handleAddNew}
          disabled={loading || isAddingNew}
        >
          <i className="fas fa-plus-circle"></i>
          <span>Add New Account</span>
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          className="form-control listing-search"
          placeholder="Search Accounts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="listing-table">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th className="text-start">Name</th>
                <th className="text-start">Type</th>
                <th className="text-start">Status</th>
                <th className="text-start">Contact Person</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <tr key={item.id} className={item.id === 'new' ? 'new-item-row' : ''}>
                  <td>
                    {editingId === item.id ? (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editedData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Enter account name"
                        autoFocus
                      />
                    ) : (
                      item.name
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <select
                        className="form-select form-select-sm"
                        value={editedData.type}
                        onChange={(e) => handleChange('type', e.target.value)}
                      >
                        <option value="Enterprise">Enterprise</option>
                        <option value="SMB">SMB</option>
                        <option value="Startup">Startup</option>
                      </select>
                    ) : (
                      item.type
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <select
                        className="form-select form-select-sm"
                        value={editedData.status}
                        onChange={(e) => handleChange('status', e.target.value)}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Pending">Pending</option>
                      </select>
                    ) : (
                      <span className={`badge bg-${item.status === 'Active' ? 'success' : 
                        item.status === 'Inactive' ? 'danger' : 'warning'}`}>
                        {item.status}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editedData.contactPerson}
                        onChange={(e) => handleChange('contactPerson', e.target.value)}
                        placeholder="Enter contact person"
                      />
                    ) : (
                      item.contactPerson
                    )}
                  </td>
                  <td>
                    <div className="action-buttons text-end">
                      {editingId === item.id ? (
                        <>
                          <button 
                            className="btn btn-outline-success btn-icon"
                            onClick={handleSave}
                            disabled={loading}
                          >
                            {loading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-check"></i>
                            )}
                          </button>
                          <button 
                            className="btn btn-outline-secondary btn-icon ms-2"
                            onClick={handleCancel}
                            disabled={loading}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            className="btn btn-outline-primary btn-icon"
                            onClick={() => handleEdit(item)}
                            disabled={loading}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button 
                            className="btn btn-outline-danger btn-icon ms-2"
                            onClick={() => handleDelete(item)}
                            disabled={loading}
                          >
                            {loading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-trash"></i>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccountsList;